import React, { useMemo, useState } from 'react';
import {
  Check,
  Copy,
  Edit3,
  Loader2,
  Mic,
  Pause,
  Play,
  Share2,
  Sparkles,
  Type,
  Video,
  Volume2,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { getUser } from '../../utils/helpers';
import { NODE_BACKEND_URL } from '../../utils/constant';

const detectMobileViewport = (search) => {
  const params = new URLSearchParams(search);
  return params.get('mobileViewport') === '1' || params.get('mobileEmulator') === '1';
};

const TEMPLATES = [
  {
    id: 'workout-intro',
    name: 'Workout Intro',
    icon: '💪',
    template: `Quick opener:\nToday we hit lower-body power and core control.\n3 focus points:\n- Tempo control\n- Full range reps\n- Finish with recovery reset`,
  },
  {
    id: 'nutrition-tip',
    name: 'Nutrition Tip',
    icon: '🥗',
    template: `Fueling tip:\nPre-session: carbs + hydration\nPost-session: protein + electrolytes\nReminder: log macros in app for daily consistency.`,
  },
  {
    id: 'form-check',
    name: 'Form Check',
    icon: '🎯',
    template: `Form check:\nCommon miss: rushing setup.\nFix: brace, stacked posture, controlled descent, full finish.\nCue: quality reps over speed.`,
  },
];

const VOICES = [
  { id: 'liam', name: 'Liam', description: 'Energetic' },
  { id: 'sarah', name: 'Sarah', description: 'Warm' },
  { id: 'daniel', name: 'Daniel', description: 'Direct' },
  { id: 'charlotte', name: 'Charlotte', description: 'Polished' },
];

export default function PublishCreatorPage() {
  const location = useLocation();
  const isMobileViewport = useMemo(() => detectMobileViewport(location.search), [location.search]);

  const [activeTab, setActiveTab] = useState('script');
  const [content, setContent] = useState('');
  const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [shareableLink, setShareableLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [teleprompterRunning, setTeleprompterRunning] = useState(false);
  const [error, setError] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [audioGenerated, setAudioGenerated] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(VOICES[0].id);

  const user = getUser();
  const userId = user?.id || user?._id || 'anonymous';

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  const applyTemplate = (template) => {
    setContent(template.template);
  };

  const handleEnhance = async (style) => {
    if (!content.trim()) return;
    setIsEnhancing(true);
    setError(null);

    try {
      const promptMap = {
        'Make Engaging': `Rewrite this script for better short-form engagement. Return script only:\n\n${content}`,
        'Make Concise': `Shorten this script for a 30-45 second clip. Return script only:\n\n${content}`,
        'Add Energy': `Rewrite this script with higher energy and urgency. Return script only:\n\n${content}`,
      };

      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: promptMap[style] || promptMap['Make Engaging'],
          history: [],
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Enhancement failed (${response.status})`);
      }

      const data = await response.json();
      const enhanced = data.data?.response || data.response || data.data?.fullContent;
      if (!enhanced) {
        throw new Error('No enhanced content returned');
      }
      setContent(enhanced.trim());
    } catch (err) {
      console.error('[PublishCreator] Enhancement error:', err);
      setError(`AI enhancement failed: ${err.message}`);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleGenerateVoice = async () => {
    if (!content.trim()) return;
    setIsGeneratingVoice(true);
    setError(null);

    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/generate-narration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exerciseName: 'Content Narration',
          exerciseData: { script: content, voice: selectedVoice },
          style: 'motivational',
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Voice generation failed (${response.status})`);
      }

      await response.json();
      setAudioGenerated(true);
    } catch (err) {
      console.error('[PublishCreator] Voice generation error:', err);
      setError(`Voice generation failed: ${err.message}`);
    } finally {
      setIsGeneratingVoice(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!content.trim()) return;
    setSavingDraft(true);
    setError(null);

    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/workouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Content Draft - ${new Date().toLocaleDateString()}`,
          sport: 'content',
          content,
          voice: selectedVoice,
          type: 'content-draft',
        }),
      });

      if (!response.ok) throw new Error('Failed to save draft');
    } catch (err) {
      console.error('[PublishCreator] Save draft error:', err);
      setError('Failed to save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!content.trim()) return;
    setError(null);

    try {
      await handleSaveDraft();
      const hash = btoa(content.slice(0, 50)).replace(/[=+/]/g, '').slice(0, 8);
      setShareableLink(`https://slam.app/content/${hash}`);
    } catch (err) {
      console.error('[PublishCreator] Publish error:', err);
      setError('Failed to publish. Please try again.');
    }
  };

  const copyLink = () => {
    if (!shareableLink) return;
    navigator.clipboard.writeText(shareableLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const cardClass = 'rounded-2xl border border-white/10 bg-[#0b1118]';
  const compactClass = isMobileViewport ? 'p-3' : 'p-4';

  return (
    <div className={`min-h-full bg-[#040608] text-zinc-100 ${isMobileViewport ? 'p-2 pb-16' : 'p-4 md:p-6'}`}>
      <div className={`${isMobileViewport ? 'space-y-2' : 'mx-auto max-w-5xl space-y-3'}`}>
        <header className={`${cardClass} ${compactClass}`}>
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl border border-pink-400/45 bg-pink-400/12 text-pink-300">
              <Share2 size={16} />
            </span>
            <div>
              <h1 className="text-[15px] font-semibold tracking-tight">Create &amp; Share</h1>
              <p className="text-[10px] text-zinc-500">Compact content workflow for mobile app review</p>
            </div>
          </div>
        </header>

        {error && (
          <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200">
            {error}
          </div>
        )}

        <section className={`${cardClass} ${compactClass} space-y-3`}>
          <div className="grid grid-cols-2 gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-1">
            <button
              type="button"
              onClick={() => setActiveTab('script')}
              className={`inline-flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] ${
                activeTab === 'script'
                  ? 'bg-pink-500/80 text-white'
                  : 'text-zinc-400'
              }`}
            >
              <Edit3 size={13} />
              Script
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('record')}
              className={`inline-flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] ${
                activeTab === 'record'
                  ? 'bg-pink-500/80 text-white'
                  : 'text-zinc-400'
              }`}
            >
              <Video size={13} />
              Record
            </button>
          </div>

          {activeTab === 'script' && (
            <div className="space-y-3">
              <div>
                <p className="mb-1 text-[10px] uppercase tracking-[0.16em] text-zinc-500">Quick Templates</p>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATES.map((template) => (
                    <button
                      type="button"
                      key={template.id}
                      onClick={() => applyTemplate(template)}
                      className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.03] px-2 py-1 text-[10px] text-zinc-200"
                    >
                      <span>{template.icon}</span>
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-2.5">
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="flex items-center gap-1 text-[11px] text-zinc-300">
                    <Type size={13} />
                    Script
                  </p>
                  <p className="text-[10px] text-zinc-500">{wordCount} words</p>
                </div>
                <textarea
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  placeholder="Write your post script..."
                  className={`w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-zinc-100 placeholder:text-zinc-500 focus:border-pink-400/60 focus:outline-none ${isMobileViewport ? 'h-44' : 'h-56'}`}
                />
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-2.5">
                <div className="mb-1.5 flex items-center gap-1 text-[11px] text-zinc-300">
                  <Sparkles size={13} />
                  AI Rewrite
                  {isEnhancing && <Loader2 size={12} className="animate-spin text-pink-300" />}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {['Make Engaging', 'Make Concise', 'Add Energy'].map((style) => (
                    <button
                      type="button"
                      key={style}
                      onClick={() => handleEnhance(style)}
                      disabled={isEnhancing || !content.trim()}
                      className="rounded-full border border-white/15 bg-white/[0.03] px-2 py-1 text-[10px] text-zinc-300 disabled:opacity-45"
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-2.5">
                <div className="mb-1.5 flex items-center gap-1 text-[11px] text-zinc-300">
                  <Volume2 size={13} />
                  Voice Narration
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {VOICES.map((voice) => (
                    <button
                      type="button"
                      key={voice.id}
                      onClick={() => setSelectedVoice(voice.id)}
                      className={`rounded-full border px-2 py-1 text-[10px] ${
                        selectedVoice === voice.id
                          ? 'border-pink-400/60 bg-pink-500/20 text-pink-100'
                          : 'border-white/15 bg-white/[0.03] text-zinc-400'
                      }`}
                    >
                      {voice.name} ({voice.description})
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleGenerateVoice}
                  disabled={isGeneratingVoice || !content.trim()}
                  className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-xl border border-pink-400/50 bg-pink-500/15 py-2 text-[11px] text-pink-200 disabled:opacity-45"
                >
                  {isGeneratingVoice ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      Generating...
                    </>
                  ) : audioGenerated ? (
                    <>
                      <Check size={13} />
                      Narration Ready
                    </>
                  ) : (
                    <>
                      <Mic size={13} />
                      Generate Voice
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'record' && (
            <div className="space-y-2">
              <div className="relative aspect-[9/16] max-h-[420px] w-full rounded-2xl border border-white/10 bg-gradient-to-b from-[#101722] to-[#080d14]">
                <div className="absolute inset-0 grid place-items-center text-center">
                  <div>
                    <Video size={38} className="mx-auto text-zinc-600" />
                    <p className="mt-2 text-[11px] text-zinc-400">Camera preview surface</p>
                  </div>
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                  <button type="button" className="h-12 w-12 rounded-full border-4 border-white/20 bg-rose-500" />
                </div>
              </div>

              {content && (
                <div className="rounded-xl border border-white/10 bg-black/20 p-2.5">
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="text-[11px] text-zinc-300">Teleprompter</p>
                    <button
                      type="button"
                      onClick={() => setTeleprompterRunning((prev) => !prev)}
                      className="rounded-lg border border-white/15 p-1 text-zinc-300"
                    >
                      {teleprompterRunning ? <Pause size={13} /> : <Play size={13} />}
                    </button>
                  </div>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-white/10 bg-white/[0.02] px-2 py-1.5 text-[11px] leading-relaxed text-zinc-200">
                    {content}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className={`${cardClass} ${compactClass} space-y-2`}>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={!content.trim() || savingDraft}
              className="inline-flex items-center justify-center gap-1 rounded-xl border border-white/15 bg-white/[0.04] py-2 text-[11px] text-zinc-200 disabled:opacity-45"
            >
              {savingDraft ? <Loader2 size={13} className="animate-spin" /> : null}
              Save Draft
            </button>
            <button
              type="button"
              onClick={handlePublish}
              disabled={!content.trim()}
              className="inline-flex items-center justify-center gap-1 rounded-xl border border-pink-400/50 bg-pink-500/15 py-2 text-[11px] text-pink-200 disabled:opacity-45"
            >
              <Share2 size={13} />
              {shareableLink ? 'Refresh Link' : 'Publish'}
            </button>
          </div>

          {shareableLink && (
            <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-2.5 py-2">
              <p className="text-[10px] text-emerald-300">Published link</p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="truncate text-[10px] text-zinc-300">{shareableLink}</p>
                <button
                  type="button"
                  onClick={copyLink}
                  className="rounded-lg border border-white/15 p-1 text-zinc-200"
                >
                  {copied ? <Check size={12} className="text-emerald-300" /> : <Copy size={12} />}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
